// ── Drag pointer-up ──
// The drag session's `onPointerUp` handler — the sibling of ./dragMove.ts, which holds
// `onPointerMove`.
//
// The phases, in the order they run:
//   1. teardown   — stage handlers off, viewport unpaused, footprint overlay hidden
//   2. guard      — is the session still valid? (marker destroyed between down and up)
//   3. tap        — no movement → outline the item and open its menu (a quick tap; a
//                   hold that was released without moving only puts the item back down)
//   4. drop       — save where the item was released: ./windowDropCommit.ts or ./dropCommit.ts
//   5. settle     — recompute z-indexes with no exclusions, outline the dropped item
//   6. release    — `finally`: un-fade, clear drag refs, record ONE undo revision
//
// The three drag maps the owning scope resets are reached through `resetDragMaps`.
// `detachStageHandlers` wraps the three `app.stage.off(...)` calls unchanged; it is invoked
// at exactly the original point, after the `if (!app) return` guard (which, being inside
// the `try`, still runs the `finally` — that is load-bearing).
//
// The type-only imports from ./dragSession do not create a runtime cycle: `import type` is
// erased at compile time, so the emitted module graph stays one-way.
import * as PIXI from 'pixi.js';
import type { QueryClient } from '@tanstack/react-query';
import { clearWallGuides } from './wallGuideDrag';
import type { HouseFurniture } from '../../../models/apiTypes';
import useInteriorHistoryStore from '../../../stores/useInteriorHistoryStore';
import { toSnapshot, roomPreviews } from '../state/history';
import useFurniturePreviewStore from '../../../stores/useFurniturePreviewStore';
import { recomputeAllZIndexes } from '../../../domain/depthMath';
import { commitNonWindowDrop } from './dropCommit';
import { commitWindowDrop } from './windowDropCommit';
import { CONFLICT_FLOOR_ZINDEX } from './dragMove';
import { redrawOccupiedTilesOverlay } from '../render/occupiedOverlay';
import { tweenFromOffset } from '../render/pickupTween';
import { impactOccurred, notificationOccurred } from '../../../utils/telegram/hapticFeedback';
import { playSfx } from '../../../utils/sfx';
import type { RenderFurniture } from '../../../domain/types';
import type { RoomSceneFurniture, RoomSceneDrag } from '../state/roomSceneState';
import type { DragSessionContext, DragSessionGates } from './dragSession';
import type { DragScope, DragSurfaceOrigin } from './dragStart';

/** How long a picked-up (but not moved) item takes to settle back down. */
const PUT_DOWN_MS = 180;

// Shared empty set for recompute calls that must apply NO carried-item exclusions.
// A module-level constant avoids allocating on every drop.
const EMPTY_CARRIED_IDS: Set<number> = new Set();

/** Values read off the live sprite at pointerdown (see dragStart.resolveDragTarget). */
export interface DragLiveFields {
  livePlacementMode: RenderFurniture['placement_mode'];
  liveWallFloorOffset: number;
}

/** Handles into the owning drag-session closure: the two things it lets this handler do. */
export interface DragGestureControls {
  detachStageHandlers: () => void;
  /** Give the camera its pan back (cameraGestures.holdCameraForDrag). */
  releaseCamera: () => void;
  resetDragMaps: () => void;
}

export interface DragPointerUpContext {
  /** The context the session itself was opened with — passed through, not re-listed. */
  session: DragSessionContext;
  live: DragLiveFields;
  gesture: DragGestureControls;
  scope: DragScope;
  origin: DragSurfaceOrigin;
}

export async function handleDragPointerUp(ctx: DragPointerUpContext): Promise<void> {
  const {
    session: {
      scene: { app },
      refs: { furniture: furnitureScene, drag, overlays, flags },
      item: { furniture },
      geometry: {
        tileDimW,
        tileDimH,
        interactionIsRug,
        isWindow,
        rawEffectiveWidthTiles,
        rawEffectiveHeightTiles,
        getLiveCatalog,
      },
      selectionOutline: { showGreenOutline, logFurnitureDiagnostics },
      radialMenu: { openFurnitureRadialMenu },
      gates,
      services: { mapObjectId, queryClient, t },
      previewActions: { updatePreviewPosition, updatePreviewPlacementMode, togglePreviewMirror },
    },
    live: { livePlacementMode, liveWallFloorOffset },
    gesture: {
      detachStageHandlers,
      releaseCamera,
      resetDragMaps,
    },
    scope: { dragSurfaceMap, dragFootprintMap, dragSurfaceItemFootprintMap, carriedItems },
    origin: { startIsInSurfaceArea, startTileX, startTileY },
  } = ctx;

 try {
  if (!app) return;
  detachStageHandlers();
  const dragState = drag.state;

  // The camera follows fingers again.
  releaseCamera();

  // Always hide drag footprint overlay on pointer up
  if (overlays.dragFp) {
    overlays.dragFp.clear();
    overlays.dragFp.visible = false;
  }
  clearWallGuides(overlays.wallGuides);

  // Check if dragState and marker still exist and are valid before accessing properties
  if (!dragState || !dragState.marker || !dragState.marker.position) {
    drag.state = null;
    drag.draggingCarriedIds = new Set();
    return;
  }

  // Use marker from dragState instead of closure to avoid stale references
  const currentMarker = dragState.marker;
  const currentSprite = currentMarker.children?.[0] as PIXI.Sprite | undefined;
  if (currentSprite) {
    currentSprite.cursor = 'grab';
  }
  // Picked up by holding, then released without moving: put it back down where it
  // was (glide down, nothing saved) — holding is not a move.
  // A second finger cancelled the drag (camera gesture): same — back where it was.
  const cancelled = !!dragState.cancelled;
  const putBackDown = dragState.isDragging && (!dragState.moved || cancelled);
  if (putBackDown) {
    const settle = (marker: PIXI.Container, x: number, y: number) => {
      const dx = x - marker.position.x;
      const dy = y - marker.position.y;
      marker.position.set(x, y);
      tweenFromOffset(marker, dx, dy, PUT_DOWN_MS);
    };
    settle(currentMarker, dragState.origX, dragState.origY);
    for (const carried of carriedItems) settle(carried.marker as PIXI.Container, carried.origX, carried.origY);
    if (currentSprite) {
      currentSprite.tint = 0xFFFFFF;
      currentSprite.alpha = 1;
    }
    impactOccurred('soft');
    playSfx('drop');
  }

  if (!cancelled && (!dragState.isDragging || putBackDown)) {
    // Short tap (no drag): show selection outline and persist it
    showGreenOutline(currentMarker);
  }
  if (!cancelled && !dragState.isDragging) {
    // A quick tap opens the item's menu (flip, store, …).
    openFurnitureRadialMenu(currentMarker, furniture, dragState.startX, dragState.startY);
    impactOccurred('medium');
  }

  if (dragState.isDragging && !putBackDown) {
    const newX = currentMarker.position.x;
    const newY = currentMarker.position.y;
    let dropHasConflict = false;

    if (isWindow) {
      dropHasConflict = !(await commitWindowDrop({
        target: { currentMarker, currentSprite, origX: dragState.origX, origY: dragState.origY },
        drop: { newX, newY },
        furniture,
        geometry: {
          tileDimW,
          tileDimH,
          interactionIsRug,
          rawEffectiveWidthTiles,
          rawEffectiveHeightTiles,
          liveWallFloorOffset,
        },
        interiorMarkers: furnitureScene.markers,
        services: { mapObjectId, queryClient, t },
        previewActions: { updatePreviewPosition, togglePreviewMirror },
        logFurnitureDiagnostics,
        dropWallOffset: dragState.wallOffset,
      }));
    } else {
      // ── Non-window furniture: normal save logic ──
      // `dropHasConflict` is the only value the block wrote that is read further down,
      // so it comes back as the return value (and, exactly as before, a throw skips
      // that read entirely).
      dropHasConflict = await commitNonWindowDrop({
        currentMarker,
        currentSprite,
        dragState,
        newX,
        newY,
        tileDimW,
        tileDimH,
        interactionIsRug,
        getLiveCatalog,
        furniture,
        dragSurfaceMap,
        dragFootprintMap,
        dragSurfaceItemFootprintMap,
        carriedItems,
        startIsInSurfaceArea,
        startTileX,
        startTileY,
        livePlacementMode,
        interiorMarkers: furnitureScene.markers,
        mapObjectId,
        queryClient,
        t,
        updatePreviewPosition,
        updatePreviewPlacementMode,
      });
    }

    redrawOccupiedTilesOverlay(furnitureScene.markers, overlays.occupiedTiles, flags.showGridMode);
    // Recompute all z-indexes after successful drop so surface ordering is consistent
    recomputeAllZIndexes(furnitureScene.markers, drag.state, drag.draggingCarriedIds, logFurnitureDiagnostics);
    logFurnitureDiagnostics(); // log final z-indexes after recompute
    if (dropHasConflict && !isWindow) {
      // Keep conflict item visible above the item it overlaps (set after recompute)
      currentMarker.zIndex = CONFLICT_FLOOR_ZINDEX;
    }
    // Put down: a firm tap on a valid spot, a warning buzz on a red one.
    if (dropHasConflict) notificationOccurred('warning');
    else impactOccurred('medium');
    playSfx('drop');
  }
  // Recompute z-indexes once more with NO exclusions (carried items were skipped in the
  // first recomputeAllZIndexes call above). This ensures surface-placed items (like a
  // nightstand on a table that was dragged) get the correct surface boost.
  //
  // The exclusion arguments MUST be literal null/empty here, not the refs. The drag refs
  // are only cleared in the `finally` block BELOW this line, so passing them would repeat
  // the very same exclusions as the first call and this "safety net" pass would be a no-op
  // — exactly the bug it was written to prevent. A dragged item and anything carried on
  // its surface would then keep the transient z the drag code assigned (owner + 1500)
  // instead of the settled surface ordering, leaving a surface item rendering at the
  // wrong depth relative to its owner until some unrelated recompute happened to fix it.
  recomputeAllZIndexes(furnitureScene.markers, null, EMPTY_CARRIED_IDS, logFurnitureDiagnostics);
  // Show selection outline on the dropped item (not after a cancelled camera gesture)
  if (!cancelled) showGreenOutline(currentMarker);
 } finally {
  releaseDragSession({
    refs: { furniture: furnitureScene, drag },
    gates,
    services: { mapObjectId, queryClient },
    resetDragMaps,
  });
 }
}

export interface DragReleaseContext {
  refs: { furniture: RoomSceneFurniture; drag: RoomSceneDrag };
  gates: DragSessionGates;
  services: { mapObjectId: number; queryClient: QueryClient };
  resetDragMaps: () => void;
}

/**
 * Always-runs cleanup (success, invalid drop, early return, or throw). Everything here is
 * a release of something the gesture took: faded markers, the drag refs, the per-drag maps
 * and the suspended undo capture — which is why it must not be reachable only on the happy
 * path.
 */
export function releaseDragSession(ctx: DragReleaseContext): void {
  const {
    refs: { furniture: furnitureScene, drag },
    gates: { canManageFurniture, houseFurnitureLoaded, houseFurnitureFetching },
    services: { mapObjectId, queryClient },
    resetDragMaps,
  } = ctx;

  // 1. Restore transparency of markers faded during drag (task 04). This MUST
  //    happen on every exit path or a faded sprite (e.g. a sofa) stays stuck at
  //    alpha 0.4 until re-dragged.
  for (const [marker, origAlpha] of drag.transparentDuringDrag.entries()) {
    marker.alpha = origAlpha;
  }
  drag.transparentDuringDrag = new Map();
  // 1b. Defensive net: reset any marker still sitting at the exact drag-fade
  //     alpha (0.4) that wasn't tracked above. 0.4 is used ONLY for drag-overlap
  //     fading — conflict/window-invalid tints use 0.5, so this is safe.
  for (const marker of furnitureScene.markers.values()) {
    if (marker.alpha === 0.4) marker.alpha = 1;
  }
  drag.state = null;
  drag.draggingCarriedIds = new Set();
  resetDragMaps(); // reset for next drag
  // 2. End the capture-suspend transaction (task 03) and record exactly ONE
  //    revision for the whole gesture. Clearing the flag here (finally) means it
  //    can never get stuck even if the handler threw or returned early.
  drag.captureSuspended = false;
  if (canManageFurniture && houseFurnitureLoaded && mapObjectId && !houseFurnitureFetching) {
    const liveItems = queryClient.getQueryData<HouseFurniture[]>(['houseFurniture', mapObjectId]) ?? [];
    const snap = toSnapshot(liveItems, roomPreviews(useFurniturePreviewStore.getState(), mapObjectId));
    const state = useInteriorHistoryStore.getState();
    if (state.mapObjectId !== mapObjectId || state.revisions.length === 0) {
      // init ignores re-init of an identical baseline; record() ignores no-ops.
      state.init(mapObjectId, snap);
    } else {
      // record() is a no-op when the snapshot equals the current revision, so a
      // drag that ended where it started doesn't push a bogus step.
      state.record(snap);
    }
  }
}
